import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Target, CheckCircle2, Flame, RotateCcw, GraduationCap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { TABLES, TABLE_THEMES } from '../lib/tabuada';
import { Mascot } from '../components/game/Mascot';

function Metric({ icon: Icon, value, label, color }) {
  return (
    <div className="card-soft flex flex-col items-center p-4">
      <Icon className="h-7 w-7" style={{ color }} />
      <p className="font-fred text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-center text-[11px] font-black uppercase tracking-wide text-slate-500">{label}</p>
    </div>
  );
}

export default function ProgressScreen({ progress, onBack, onReset }) {
  const totals = progress.totals || { answered: 0, correct: 0, wrong: 0 };
  const learnedCount = Object.values(progress.tables || {}).filter((t) => t.learned).length;

  const chartData = TABLES.map((n) => {
    const t = progress.tables?.[n] || {};
    const answered = t.answered || 0;
    const acc = answered ? Math.round(((t.correct || 0) / answered) * 100) : 0;
    return { name: `${n}`, acc, answered, stars: t.stars || 0, color: TABLE_THEMES[n].bg };
  });

  const needPractice = chartData.filter((d) => d.answered > 0 && d.acc < 70).map((d) => d.name);

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 pb-10">
      <div className="mb-3 flex items-center justify-between">
        <button
          data-testid="progress-back-btn"
          onClick={onBack}
          className="btn-3d bg-white px-3 py-2 text-purple-700"
          style={{ borderColor: '#e2e8f0' }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-fred text-2xl font-bold text-indigo-900 sm:text-3xl">Meu Progresso</h1>
        <div className="w-11" />
      </div>

      <div className="mb-4 flex items-center justify-center">
        <Mascot mood="happy" size={110} />
        <p className="ml-2 max-w-[200px] font-fred text-lg font-bold text-purple-700">
          {progress.name ? `Você é demais, ${progress.name}!` : 'Continue aprendendo!'}
        </p>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric icon={GraduationCap} value={learnedCount} label="Tabuadas aprendidas" color="#3B82F6" />
        <Metric icon={CheckCircle2} value={totals.correct} label="Acertos" color="#10B981" />
        <Metric icon={Target} value={totals.answered} label="Exercícios" color="#8B5CF6" />
        <Metric icon={Flame} value={progress.bestStreak || 0} label="Melhor sequência" color="#F97316" />
      </div>

      <div className="card-soft mb-5 p-4">
        <h2 className="mb-2 flex items-center gap-2 font-fred text-lg font-bold text-indigo-900">
          <Star className="h-5 w-5 text-amber-400" /> Acertos por tabuada (%)
        </h2>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%" minHeight={180}>
            <BarChart data={chartData} margin={{ top: 10, right: 6, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontWeight: 800, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                formatter={(v) => [`${v}%`, 'Acertos']}
                contentStyle={{ borderRadius: 16, border: '2px solid #e2e8f0', fontWeight: 700 }}
              />
              <Bar dataKey="acc" radius={[10, 10, 0, 0]}>
                {chartData.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-soft mb-5 p-4">
        <h2 className="mb-2 font-fred text-lg font-bold text-indigo-900">💪 Precisam de mais prática</h2>
        {needPractice.length ? (
          <div className="flex flex-wrap gap-2">
            {needPractice.map((n) => (
              <span
                key={n}
                className="rounded-full px-4 py-2 font-fred text-lg font-bold text-white shadow"
                style={{ background: TABLE_THEMES[n].bg }}
              >
                Tabuada do {n}
              </span>
            ))}
          </div>
        ) : (
          <p className="font-bold text-emerald-600">🎉 Você está indo muito bem em tudo!</p>
        )}
      </div>

      <button
        data-testid="reset-progress-btn"
        onClick={onReset}
        className="btn-3d mx-auto bg-white px-5 py-3 text-sm text-rose-500"
        style={{ borderColor: '#fecaca' }}
      >
        <RotateCcw className="h-4 w-4" /> Recomeçar meu progresso
      </button>
    </div>
  );
}
